import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_PAYMENT_RULES, type PaymentRuleConfig } from '../shared/lib/businessRules/paymentRules';
import { DEFAULT_SHIPPING_RULES, type ShippingRule } from '../shared/lib/businessRules/shippingRules';
import type { BankAccount } from '../shared/lib/businessRules/bankAccounts';

interface SettingsState {
  paymentRules: PaymentRuleConfig;
  shippingRules: ShippingRule[];
  bankAccounts: BankAccount[];

  // Site settings
  siteName: string;
  phoneNumber: string;
  phone2: string;
  phoneEmergency: string;
  fax: string;
  email: string;
  email2: string;
  address: string;
  mapAddress: string;
  whatsappNumber: string;
  tawkToPropertyId: string;
  instagram: string;
  facebook: string;
  youtube: string;
  twitter: string;
  workingHoursWeekday: string;
  workingHoursSaturday: string;
  workingHoursSunday: string;

  // Categories
  categories: any[];
  setCategories: (categories: any[]) => void;

  // Actions
  updatePaymentRules: (rules: Partial<PaymentRuleConfig>) => void;
  updateShippingRules: (rules: ShippingRule[]) => void;
  addBankAccount: (account: BankAccount) => void;
  updateBankAccount: (id: string, data: Partial<BankAccount>) => void;
  deleteBankAccount: (id: string) => void;
  setActiveBankAccount: (id: string) => void;
  updateSiteSettings: (settings: Partial<Pick<SettingsState,
    'siteName' | 'phoneNumber' | 'phone2' | 'phoneEmergency' | 'fax' |
    'email' | 'email2' | 'address' | 'mapAddress' | 'whatsappNumber' | 'tawkToPropertyId' |
    'instagram' | 'facebook' | 'youtube' | 'twitter' |
    'workingHoursWeekday' | 'workingHoursSaturday' | 'workingHoursSunday'
  >>) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      paymentRules: DEFAULT_PAYMENT_RULES,
      shippingRules: DEFAULT_SHIPPING_RULES,
      bankAccounts: [
        {
          id: 'bank-1',
          bankName: 'Vakıfbank',
          branchName: 'SARNIÇ 1090',
          accountHolderName: 'SB İKLİMLENDİRME DAYANIKLI TÜKETİM MALLARI İNŞAAT GIDA VE OTO SANAYİ DIŞ TİCARET LTD. ŞTİ.',
          iban: 'TR62 0001 5001 5800 7317 5272 15',
          accountNumber: '1090-158007317527215',
          isActive: true,
        },
        {
          id: 'bank-2',
          bankName: 'Ziraat Bankası',
          branchName: 'KARŞIYAKA 411',
          accountHolderName: 'SB İKLİMLENDİRME DAYANIKLI TÜKETİM MALLARI İNŞAAT GIDA VE OTO SANAYİ DIŞ TİCARET LTD. ŞTİ.',
          iban: 'TR60 0001 0004 1188 2220 7450 01',
          accountNumber: '411-18822074500',
          isActive: false,
        },
        {
          id: 'bank-3',
          bankName: 'Akbank',
          branchName: 'GİRNE BULVARI 0597',
          accountHolderName: 'SB İKLİMLENDİRME DAYANIKLI TÜKETİM MALLARI İNŞAAT GIDA VE OTO SANAYİ DIŞ TİCARET LTD. ŞTİ.',
          iban: 'TR18 0004 6005 9788 8000 0610 49',
          accountNumber: '0597-59788800061049',
          isActive: false,
        },
        {
          id: 'bank-4',
          bankName: 'Garanti Bankası',
          branchName: 'GİRNE CADDESİ 712',
          accountHolderName: 'SB İKLİMLENDİRME DAYANIKLI TÜKETİM MALLARI İNŞAAT GIDA VE OTO SANAYİ DIŞ TİCARET LTD. ŞTİ.',
          iban: 'TR25 0006 2000 7120 0006 2967 64',
          accountNumber: '712-712000629674',
          isActive: false,
        },
        {
          id: 'bank-5',
          bankName: 'İş Bankası',
          branchName: 'NERGİS 3436',
          accountHolderName: 'SB İKLİMLENDİRME DAYANIKLI TÜKETİM MALLARI İNŞAAT GIDA VE OTO SANAYİ DIŞ TİCARET LTD. ŞTİ.',
          iban: 'TR25 0006 4000 0013 4360 5889 68',
          accountNumber: '3436-13436058896',
          isActive: false,
        },
        {
          id: 'bank-6',
          bankName: 'Yapı Kredi',
          branchName: 'NARLIDERE 203',
          accountHolderName: 'SB İKLİMLENDİRME DAYANIKLI TÜKETİM MALLARI İNŞAAT GIDA VE OTO SANAYİ DIŞ TİCARET LTD. ŞTİ.',
          iban: 'TR61 0006 7010 0000 0056 2982 85',
          accountNumber: '203-100000056298285',
          isActive: false,
        },
        {
          id: 'bank-7',
          bankName: 'QNB Finansbank',
          branchName: 'GİRNE BULVARI 1408',
          accountHolderName: 'SB İKLİMLENDİRME DAYANIKLI TÜKETİM MALLARI İNŞAAT GIDA VE OTO SANAYİ DIŞ TİCARET LTD. ŞTİ.',
          iban: 'TR44 0011 1000 0000 0084 1238 39',
          accountNumber: '1408-1000000841238 39',
          isActive: false,
        },
      ],

      siteName: 'SB Klima',
      phoneNumber: '0232 504 07 01',
      phone2: '',
      phoneEmergency: '0850 000 00 00',
      fax: '',
      email: 'info@sbklima.com',
      email2: '',
      address: 'İzmir, Türkiye',
      mapAddress: '',
      whatsappNumber: '905321234567',
      tawkToPropertyId: 'PROPERTY_ID',
      instagram: '',
      facebook: '',
      youtube: '',
      twitter: '',
      workingHoursWeekday: '09:00 - 18:00',
      workingHoursSaturday: '09:00 - 13:00',
      workingHoursSunday: 'Kapalı',

      categories: [],
      setCategories: (categories) => set({ categories }),

      updatePaymentRules: (rules) =>
        set((state) => ({
          paymentRules: { ...state.paymentRules, ...rules },
        })),

      updateShippingRules: (rules) => set({ shippingRules: rules }),

      addBankAccount: (account) =>
        set((state) => ({
          bankAccounts: [...state.bankAccounts, account],
        })),

      updateBankAccount: (id, data) =>
        set((state) => ({
          bankAccounts: state.bankAccounts.map((b) =>
            b.id === id ? { ...b, ...data } : b
          ),
        })),

      deleteBankAccount: (id) =>
        set((state) => ({
          bankAccounts: state.bankAccounts.filter((b) => b.id !== id),
        })),

      setActiveBankAccount: (id) =>
        set((state) => ({
          bankAccounts: state.bankAccounts.map((b) => ({
            ...b,
            isActive: b.id === id,
          })),
        })),

      updateSiteSettings: (settings) => {
        set((state) => ({ ...state, ...settings }));
        // Also persist to backend (non-blocking)
        import('../api/client').then(({ apiService }) => {
          apiService.adminUpdateSettings(settings).catch((err) => {
            console.warn('Failed to persist settings to backend:', err);
          });
        });
      },
    }),
    {
      name: 'ar-klima-settings',
    }
  )
);
